import { useState } from "react";
import { Sparkles, BellRing, Lock, ShieldCheck, ChevronRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * BetaWelcomeModal — 4-step welcome experience for beta testers.
 * Step 1: Welcome (what is Revela)
 * Step 2: How check-ins work
 * Step 3: Privacy reassurance + beta context
 * Step 4: Consent (required) + start
 *
 * Shown at first login when user.onboarding_completed === false.
 * Triggers POST /api/auth/accept-beta-consent on completion.
 */
const BetaWelcomeModal = ({ open, userName, onComplete }) => {
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!consent) {
      toast.error("Per usare il beta devi accettare l'informativa privacy");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/accept-beta-consent", { accepted: true });
      // Also mark the legacy disclaimer as completed to avoid showing a second modal.
      try {
        localStorage.setItem("life_planner_onboarding_completed", "true");
        localStorage.setItem("yourlifemap_onboarding_complete", "true");
      } catch { /* ignore quota / private mode */ }
      toast.success("Benvenuta! Buoni check-in 🌱");
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const firstName = (userName || "").split(" ")[0] || "ciao";

  const slides = [
    {
      icon: Sparkles,
      title: `Benvenuta, ${firstName} 🌱`,
      body: (
        <>
          <p>Grazie per partecipare al beta di <strong>Revela</strong>.</p>
          <p className="text-sm text-muted-foreground">
            Revela è un'app di <strong>Experience Tracking</strong>: ti chiede brevi check-in
            durante la giornata per capire <em>come stai vivendo</em> le tue situazioni quotidiane.
            Più dati raccoglierai, più chiari saranno i pattern che emergeranno.
          </p>
        </>
      ),
    },
    {
      icon: BellRing,
      title: "Come funziona un check-in",
      body: (
        <>
          <p>Quando ricevi una notifica (o quando vuoi tu), apri l'app e in <strong>30 secondi</strong>:</p>
          <ul className="text-sm space-y-1.5 mt-2">
            <li>📍 <strong>Dove</strong> eri appena prima</li>
            <li>👥 <strong>Con chi</strong> eri</li>
            <li>🎯 Cosa <strong>stavi facendo</strong></li>
            <li>💭 <strong>Come</strong> stavi vivendo quella situazione (5 brevi domande)</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-3">
            Fai i check-in che riesci. Anche 3-5 al giorno sono utili. L'app non ti giudica.
          </p>
        </>
      ),
    },
    {
      icon: Lock,
      title: "I tuoi dati sono tuoi",
      body: (
        <>
          <p className="text-sm">Per stare tranquilla:</p>
          <ul className="text-sm space-y-1.5 mt-2">
            <li>🔒 <strong>Solo tu</strong> vedi i tuoi check-in, le tue note vocali, le tue analisi</li>
            <li>📊 Laura (admin) vede solo <strong>statistiche aggregate anonime</strong> — mai i tuoi dati individuali</li>
            <li>🔐 Tutto è cifrato (AES-256) e ospitato in EU</li>
            <li>🗑️ Puoi <strong>cancellare il tuo account</strong> in qualsiasi momento dalle Impostazioni</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-3">
            Per qualsiasi dubbio scrivi a Laura direttamente — sei in una piccola comunità di beta tester (4 persone), non un esperimento di massa.
          </p>
        </>
      ),
    },
    {
      icon: ShieldCheck,
      title: "Informativa & consenso",
      body: (
        <>
          <p className="text-sm">
            Prima di iniziare, leggi e accetta l'informativa privacy (conforme GDPR e CNOP).
          </p>
          <div className="rounded-lg border border-muted bg-muted/30 p-3 max-h-40 overflow-y-auto text-xs space-y-2">
            <p>
              <strong>Titolare del trattamento:</strong> Dott.ssa Laura Piccinini —
              piccinini.ps@gmail.com.
            </p>
            <p>
              <strong>Finalità:</strong> beta test di un'app di Experience Tracking per
              ricerca clinica preliminare. I tuoi dati individuali sono utilizzati
              <em> solo da te</em>. Laura accede esclusivamente a statistiche aggregate
              anonime (k≥5).
            </p>
            <p>
              <strong>Base giuridica:</strong> consenso (Art. 6.1.a GDPR) — revocabile in
              qualsiasi momento cancellando l'account.
            </p>
            <p>
              <strong>Diritti:</strong> accesso, rettifica, cancellazione, portabilità
              (esporta CSV da Impostazioni), opposizione, reclamo al Garante.
            </p>
            <p>
              <a href="/privacy-beta" target="_blank" rel="noreferrer" className="text-rose-600 underline">
                Leggi l'informativa completa →
              </a>
            </p>
          </div>
          <label
            className={`flex items-start gap-2.5 cursor-pointer pt-2 p-3 rounded-lg border-2 transition-colors ${
              consent
                ? "border-rose-400 bg-rose-50"
                : "border-amber-300 bg-amber-50 animate-pulse"
            }`}
            data-testid="consent-checkbox-wrap"
          >
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              data-testid="consent-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm leading-snug">
              Ho letto l'informativa e <strong>acconsento</strong> al trattamento dei miei
              dati per la finalità di beta test.
              {!consent && (
                <span className="block text-xs text-amber-700 mt-1 font-medium">
                  ↑ Spunta questa casella per attivare il pulsante "Accetto e inizio".
                </span>
              )}
            </span>
          </label>
        </>
      ),
    },
  ];

  const current = slides[step];
  const Icon = current.icon;
  const isLast = step === slides.length - 1;

  return (
    <Dialog open={open} onOpenChange={() => { /* not closable except via accept */ }}>
      <DialogContent
        className="max-w-md sm:max-w-lg p-0 overflow-hidden gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
        data-testid="beta-welcome-modal"
      >
        {/* a11y: screen-reader-only title + description (Radix requires) */}
        <DialogTitle className="sr-only">Benvenuta nel beta Revela</DialogTitle>
        <DialogDescription className="sr-only">
          Procedura guidata in 4 passi per familiarizzare con l'Experience Tracking e
          firmare il consenso al trattamento dati.
        </DialogDescription>

        {/* progress bar + step counter */}
        <div className="p-3 bg-rose-50 space-y-1.5">
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <div
                key={`welcome-prog-${i}`}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-rose-500" : "bg-rose-200"
                }`}
              />
            ))}
          </div>
          <p className="text-[11px] text-rose-700/80 text-center font-medium tracking-wide" data-testid="welcome-step-counter">
            Passo {step + 1} di {slides.length}
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold leading-tight" data-testid="welcome-title">
              {current.title}
            </h2>
          </div>

          <div className="space-y-3 min-h-[180px]" data-testid={`welcome-step-${step}`}>
            {current.body}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={submitting}
                data-testid="welcome-back-btn"
              >
                Indietro
              </Button>
            )}
            {!isLast ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                className="flex-1 bg-rose-500 hover:bg-rose-600"
                data-testid="welcome-next-btn"
              >
                Continua <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleAccept}
                disabled={!consent || submitting}
                className="flex-1 bg-rose-500 hover:bg-rose-600"
                data-testid="welcome-accept-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                {submitting ? "Salvo..." : "Accetto e inizio"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BetaWelcomeModal;
